import { markAmplifyConfigured } from '@/lib/amplify-ready';

/** Mock API removed — always returns false. Kept for backward compatibility. */
export function useMockApi(): boolean {
  return false;
}

function hasBakedAmplifyEnv(): boolean {
  return Boolean(
    import.meta.env.PROD &&
      import.meta.env.VITE_GRAPHQL_ENDPOINT &&
      import.meta.env.VITE_USER_POOL_ID &&
      import.meta.env.VITE_USER_POOL_CLIENT_ID &&
      import.meta.env.VITE_AWS_REGION,
  );
}

function buildConfigFromViteEnv(): Parameters<typeof import('aws-amplify').Amplify.configure>[0] {
  const region = import.meta.env.VITE_AWS_REGION as string;
  const userPoolId = import.meta.env.VITE_USER_POOL_ID as string;
  const userPoolClientId = import.meta.env.VITE_USER_POOL_CLIENT_ID as string;
  const graphqlEndpoint = import.meta.env.VITE_GRAPHQL_ENDPOINT as string;
  const apiKey = import.meta.env.VITE_GRAPHQL_API_KEY as string | undefined;
  const bucket = import.meta.env.VITE_S3_BUCKET as string | undefined;

  return {
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
      },
    },
    API: {
      GraphQL: {
        endpoint: graphqlEndpoint,
        region,
        defaultAuthMode: apiKey ? 'apiKey' : 'userPool',
        ...(apiKey ? { apiKey } : {}),
      },
    },
    ...(bucket
      ? {
          Storage: {
            S3: {
              bucket,
              region,
            },
          },
        }
      : {}),
  };
}

const OUTPUTS_FETCH_TIMEOUT_MS = 8_000;

async function fetchAmplifyOutputs(url: string, cache: RequestCache): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), OUTPUTS_FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { cache, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

/** Full outputs include model introspection — required before AppSync list/create calls work. */
async function hydrateFullOutputs(
  Amplify: typeof import('aws-amplify').Amplify,
): Promise<boolean> {
  try {
    const res = await fetchAmplifyOutputs('/amplify_outputs.json', 'default');
    if (!res.ok) return false;
    const outputs = await res.json();
    Amplify.configure(outputs);
    const { resetDataClient, assertDataClientReady } = await import('@/lib/amplify-client');
    resetDataClient();
    try {
      await assertDataClientReady();
    } catch (e) {
      console.warn('[ShopGND] Data client introspection check failed', e);
    }
    const { invalidateApiConfigCache } = await import('@/lib/api-config');
    invalidateApiConfigCache();
    const { notifyCatalogRefresh } = await import('@/lib/catalog-refresh');
    notifyCatalogRefresh();
    return true;
  } catch (e) {
    console.warn('[ShopGND] amplify_outputs.json load failed', e);
    return false;
  }
}

/**
 * Configure Amplify from baked VITE_* (production) or `amplify_outputs.json` (dev / fallback).
 */
export async function configureAmplify(
  Amplify: typeof import('aws-amplify').Amplify,
): Promise<void> {
  if (hasBakedAmplifyEnv()) {
    // Baked VITE_* has endpoint/auth only — catalog needs model_introspection from outputs.
    if (await hydrateFullOutputs(Amplify)) {
      markAmplifyConfigured();
      return;
    }
    Amplify.configure(buildConfigFromViteEnv());
    const { resetDataClient } = await import('@/lib/amplify-client');
    resetDataClient();
    console.warn(
      '[ShopGND] Catalog API unavailable — could not load amplify_outputs.json (model introspection).',
    );
    markAmplifyConfigured();
    return;
  }

  try {
    const cacheBust = import.meta.env.DEV ? `?t=${Date.now()}` : '';
    const res = await fetchAmplifyOutputs(`/amplify_outputs.json${cacheBust}`, 'no-store');
    if (res.ok) {
      const outputs = await res.json();
      Amplify.configure(outputs);
      const { resetDataClient, assertDataClientReady } = await import('@/lib/amplify-client');
      resetDataClient();
      try {
        await assertDataClientReady();
      } catch (e) {
        console.warn('[ShopGND] Data client introspection check failed', e);
      }
      markAmplifyConfigured();
      return;
    }
  } catch {
    /* network / missing file */
  }

  if (!hasBakedAmplifyEnv()) {
    console.warn(
      '[ShopGND] Amplify not configured — set env vars or add public/amplify_outputs.json.',
    );
    markAmplifyConfigured();
    return;
  }

  markAmplifyConfigured();
}
